// LapseRecoverySheet — Marlatt's AVE (abstinence-violation-effect)
// protocol as a 3-step dialog. Fires when today's dose total first
// crosses target for the day.
//
// The point: a slip is data, not a verdict. The flow normalizes →
// captures functional analysis → offers 60s of breathwork → asks how
// to adjust the taper. The consistency curve absorbs the day without
// resetting; the streak day-count may drop, but the user isn't told
// "you lost it."
//
// Phase 19 will swap the inline breath step for the BreatheScreen
// route; for Phase 18 the breath section is an inline 60s timer with
// a soft expanding/contracting circle synced to the cyclic-sigh
// pattern (4s double-inhale → 6s extended exhale).

import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Chip } from '../../components/ui/Chip';
import { useToast } from '../../components/ui/Toast';
import { useReducedMotion } from '../../lib/motion';
import { apiPost } from '../../lib/api';
import { apiErrorMessage, isQueuedOffline } from '../../lib/apiErrors';

type Where = 'home' | 'work' | 'out' | 'car' | 'other';
type Who = 'alone' | 'partner' | 'family' | 'friends' | 'coworkers' | 'strangers';
type Feeling = 'anxious' | 'restless' | 'lonely' | 'bored' | 'pain' | 'fomo' | 'autopilot';
type WhatHelped = 'breath' | 'walk' | 'talk' | 'sleep' | 'eat' | 'wait' | 'other';
type TaperAction = 'same' | 'extend_1w' | 'extend_2w' | 'restart';

interface LapseRecoverySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WHERE_OPTIONS: Where[] = ['home', 'work', 'out', 'car', 'other'];
const WHO_OPTIONS: Who[] = ['alone', 'partner', 'family', 'friends', 'coworkers', 'strangers'];
const FEELING_OPTIONS: Feeling[] = ['anxious', 'restless', 'lonely', 'bored', 'pain', 'fomo', 'autopilot'];
const HELP_OPTIONS: WhatHelped[] = ['breath', 'walk', 'talk', 'sleep', 'eat', 'wait', 'other'];

export function LapseRecoverySheet({ open, onOpenChange }: LapseRecoverySheetProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [whereV, setWhereV] = useState<Where | null>(null);
  const [whoV, setWhoV] = useState<Who | null>(null);
  const [feelingV, setFeelingV] = useState<Feeling | null>(null);
  const [helpedV, setHelpedV] = useState<WhatHelped | null>(null);
  const [taperAction, setTaperAction] = useState<TaperAction>('same');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset state when closed so reopen starts fresh.
      const t = window.setTimeout(() => {
        setStep(1);
        setWhereV(null);
        setWhoV(null);
        setFeelingV(null);
        setHelpedV(null);
        setTaperAction('same');
      }, 240);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  async function save() {
    setBusy(true);
    try {
      await apiPost('/api/lapse', {
        where: whereV ?? undefined,
        who: whoV ?? undefined,
        feeling: feelingV ?? undefined,
        whatHelped: helpedV ?? undefined,
        taperAction,
      });
      if (taperAction !== 'same') {
        // The lapse row is the clinically important capture and is already
        // saved. Applying the taper change is a best-effort second step:
        // if it fails we keep the lapse and tell the user where to retry,
        // rather than losing the functional analysis over curve math.
        try {
          await apiPost('/api/taper/recompute', { action: taperAction });
          await queryClient.invalidateQueries({ queryKey: ['today', 'taper'] });
          toast.push(
            taperAction === 'restart'
              ? 'Saved. Fresh taper from today.'
              : 'Saved. Your taper is gentler from here.',
          );
        } catch (taperErr) {
          toast.push(
            apiErrorMessage(
              taperErr,
              "Saved, but couldn't adjust the taper — try again from the Taper screen.",
            ),
            { tone: 'error' },
          );
        }
      } else {
        toast.push('Saved. Today is part of the data.');
      }
      onOpenChange(false);
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not save — try again.'), { tone: 'error' });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content lapse-sheet" aria-describedby={undefined}>
          <div className="dialog-handle" aria-hidden="true" />
          <Dialog.Title className="dialog-title">Today is data, not a verdict.</Dialog.Title>
          <p className="muted" style={{ marginBlockStart: 'var(--space-2)', marginBlockEnd: 'var(--space-4)' }}>
            Tell me what happened. This is how the math improves.
          </p>

          {step === 1 ? (
            <LapseStepFunctional
              whereV={whereV}
              setWhereV={setWhereV}
              whoV={whoV}
              setWhoV={setWhoV}
              feelingV={feelingV}
              setFeelingV={setFeelingV}
              helpedV={helpedV}
              setHelpedV={setHelpedV}
              onNext={() => setStep(2)}
              onSkip={() => setStep(3)}
            />
          ) : null}

          {step === 2 ? (
            <LapseStepBreathe onDone={() => setStep(3)} onSkip={() => setStep(3)} />
          ) : null}

          {step === 3 ? (
            <LapseStepRecompute
              value={taperAction}
              onChange={setTaperAction}
              onSave={() => void save()}
              onBack={() => setStep(1)}
              busy={busy}
            />
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function LapseStepFunctional({
  whereV,
  setWhereV,
  whoV,
  setWhoV,
  feelingV,
  setFeelingV,
  helpedV,
  setHelpedV,
  onNext,
  onSkip,
}: {
  whereV: Where | null;
  setWhereV: (v: Where) => void;
  whoV: Who | null;
  setWhoV: (v: Who) => void;
  feelingV: Feeling | null;
  setFeelingV: (v: Feeling) => void;
  helpedV: WhatHelped | null;
  setHelpedV: (v: WhatHelped) => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="lapse-step">
      <h3 className="text-h3">Where?</h3>
      <div className="lapse-chip-row">
        {WHERE_OPTIONS.map((v) => (
          <Chip key={v} as="button" state={whereV === v ? 'active' : 'default'} onClick={() => setWhereV(v)}>
            {v}
          </Chip>
        ))}
      </div>

      <h3 className="text-h3">Who with?</h3>
      <div className="lapse-chip-row">
        {WHO_OPTIONS.map((v) => (
          <Chip key={v} as="button" state={whoV === v ? 'active' : 'default'} onClick={() => setWhoV(v)}>
            {v}
          </Chip>
        ))}
      </div>

      <h3 className="text-h3">Feeling?</h3>
      <div className="lapse-chip-row">
        {FEELING_OPTIONS.map((v) => (
          <Chip key={v} as="button" state={feelingV === v ? 'active' : 'default'} onClick={() => setFeelingV(v)}>
            {v}
          </Chip>
        ))}
      </div>

      <h3 className="text-h3">What helped before?</h3>
      <div className="lapse-chip-row">
        {HELP_OPTIONS.map((v) => (
          <Chip key={v} as="button" state={helpedV === v ? 'active' : 'default'} onClick={() => setHelpedV(v)}>
            {v}
          </Chip>
        ))}
      </div>

      <div className="row" style={{ justifyContent: 'space-between', marginBlockStart: 'var(--space-4)' }}>
        <Button variant="tertiary" size="md" onClick={onSkip}>
          Skip
        </Button>
        <Button size="lg" onClick={onNext}>
          Continue
        </Button>
      </div>
    </div>
  );
}

// 60s inline cyclic-sighing timer (placeholder for the Phase 19
// BreatheScreen). Pattern: 4-second double-inhale → 6-second extended
// exhale. The Balban et al. 2023 study found this curve shifts
// autonomic state faster than box breathing or mindful meditation —
// the right tool at the peak-craving moment.
function LapseStepBreathe({ onDone, onSkip }: { onDone: () => void; onSkip: () => void }) {
  const reduce = useReducedMotion();
  const [secondsLeft, setSecondsLeft] = useState(60);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    startRef.current = Date.now();
    const id = window.setInterval(() => {
      const elapsed = (Date.now() - (startRef.current ?? Date.now())) / 1000;
      const left = Math.max(0, 60 - Math.floor(elapsed));
      setSecondsLeft(left);
      if (left === 0) window.clearInterval(id);
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="lapse-step lapse-breathe">
      <h3 className="text-h3">Breathe with me</h3>
      <p className="muted" style={{ marginBlockEnd: 'var(--space-4)' }}>
        Two short inhales through the nose. A long exhale through the mouth. Repeat for 60 seconds.
      </p>
      <div className="lapse-breathe-stage">
        {reduce ? (
          <div className="lapse-breathe-static" />
        ) : (
          <motion.div
            className="lapse-breathe-circle"
            animate={{ scale: [0.9, 1.05, 1.15, 0.9] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
        <div className="lapse-breathe-timer">{secondsLeft}s</div>
      </div>
      <div className="row" style={{ justifyContent: 'space-between', marginBlockStart: 'var(--space-4)' }}>
        <Button variant="tertiary" size="md" onClick={onSkip}>
          Skip
        </Button>
        <Button size="lg" onClick={onDone} disabled={secondsLeft > 0}>
          {secondsLeft > 0 ? `Continue in ${secondsLeft}s` : 'Continue'}
        </Button>
      </div>
    </div>
  );
}

function LapseStepRecompute({
  value,
  onChange,
  onSave,
  onBack,
  busy,
}: {
  value: TaperAction;
  onChange: (v: TaperAction) => void;
  onSave: () => void;
  onBack: () => void;
  busy: boolean;
}) {
  return (
    <div className="lapse-step">
      <h3 className="text-h3">Recompute the taper from here?</h3>
      <p className="muted" style={{ marginBlockEnd: 'var(--space-4)' }}>
        Your current streak may reset, but your money saved, hours reclaimed, and longest streak don't. A slip is data, not a redo. Pick what feels honest — you can change it any time.
      </p>
      <div className="stack" style={{ '--stack-gap': 'var(--space-2)' } as React.CSSProperties}>
        <LapseOption
          checked={value === 'same'}
          label="Same plan — keep going from today"
          caption="Recommended for an isolated slip. The consistency score absorbs it."
          onClick={() => onChange('same')}
        />
        <LapseOption
          checked={value === 'extend_1w'}
          label="Extend by 1 week"
          caption="A gentler curve from here. End-date shifts by 7 days."
          onClick={() => onChange('extend_1w')}
        />
        <LapseOption
          checked={value === 'extend_2w'}
          label="Extend by 2 weeks"
          caption="Much gentler. End-date shifts by 14 days."
          onClick={() => onChange('extend_2w')}
        />
        <LapseOption
          checked={value === 'restart'}
          label="Reset baseline — start a fresh taper"
          caption="Only pick this if today's dose is close to where you started."
          onClick={() => onChange('restart')}
        />
      </div>
      <div className="row" style={{ justifyContent: 'space-between', marginBlockStart: 'var(--space-5)' }}>
        <Button variant="tertiary" size="md" onClick={onBack}>
          Back
        </Button>
        <Button size="lg" onClick={onSave} loading={busy}>
          Save
        </Button>
      </div>
    </div>
  );
}

function LapseOption({
  checked,
  label,
  caption,
  onClick,
}: {
  checked: boolean;
  label: string;
  caption: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={checked}
      className={`lapse-option${checked ? ' lapse-option-checked' : ''}`}
    >
      <span className="lapse-option-radio" aria-hidden="true">
        {checked ? '●' : '○'}
      </span>
      <span className="lapse-option-text">
        <span className="lapse-option-label">{label}</span>
        <span className="lapse-option-caption">{caption}</span>
      </span>
    </button>
  );
}
